import { useEffect } from "react";
import { Phone, MessageCircle, Mail, Globe, MapPin, UserPlus } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const buildVcf = (c) => {
  const parts = c.name.trim().split(/\s+/);
  const last = parts.length > 1 ? parts.pop() : "";
  const first = parts.join(" ");
  const cityParts = c.city.trim().split(/\s+/);
  const zip = cityParts.length > 1 ? cityParts.shift() : "";
  const city = cityParts.join(" ");
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${last};${first};;;`,
    `FN:${c.name}`,
    `ORG:${c.company}`,
    `ADR;TYPE=WORK:;;${c.street};${city};;${zip};Deutschland`,
    `TEL;TYPE=CELL,VOICE:${c.phone.replace(/\s+/g, "")}`,
    `EMAIL;TYPE=INTERNET:${c.email}`,
    `URL:${c.website}`,
    `NOTE:${c.tagline}`,
    "END:VCARD",
  ].join("\r\n");
};

const ActionButton = ({ href, onClick, icon: Icon, label, sub, primary = false, center = true, testid }) => (
  <a
    href={href}
    onClick={onClick}
    target={href && href.startsWith("http") ? "_blank" : undefined}
    rel={href && href.startsWith("http") ? "noopener noreferrer" : undefined}
    data-testid={testid}
    className={`group flex min-h-16 w-full items-center gap-4 border px-5 py-4 transition-colors duration-300 ${
      center ? "justify-center text-center" : "text-left"
    } ${
      primary
        ? "border-primary bg-primary text-primary-foreground hover:bg-foreground hover:text-background hover:border-foreground"
        : "border-foreground/15 text-foreground hover:border-primary hover:text-primary"
    }`}
  >
    <Icon className="h-5 w-5 shrink-0" />
    <span className="flex min-w-0 flex-col">
      <span className="font-heading text-base font-bold uppercase tracking-wide">{label}</span>
      <span
        className={`truncate font-mono text-[11px] tracking-wider ${
          primary ? "text-primary-foreground/70 group-hover:text-background/70" : "text-muted-foreground"
        }`}
      >
        {sub}
      </span>
    </span>
  </a>
);

export default function Visitenkarte() {
  const content = useContent();
  const card = content.businessCard;

  useEffect(() => {
    document.title = `Visitenkarte · ${card.company}`;
  }, [card.company]);

  const downloadVcard = (e) => {
    e.preventDefault();
    const blob = new Blob([buildVcf(card)], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${card.company.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-6 py-14 font-body text-foreground"
      data-testid="visitenkarte-page"
    >
      <div className="w-full max-w-md text-center">
        <img
          src="/rundum-logo.png"
          alt={`${card.company} Logo`}
          className="mx-auto h-14 w-auto"
          data-testid="vcard-logo"
        />

        {card.photo_url && (
          <img
            src={card.photo_url}
            alt={card.name}
            className="mx-auto mt-8 block aspect-square w-32 border border-foreground/15 object-cover"
            data-testid="vcard-photo"
          />
        )}

        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-primary" data-testid="vcard-company">
          {card.company} · Aachen
        </p>
        <h1
          className="mt-3 font-heading text-4xl font-black tracking-tight text-foreground sm:text-5xl"
          data-testid="vcard-name"
        >
          {card.name}
        </h1>
        <p className="mt-2 text-base text-muted-foreground" data-testid="vcard-tagline">
          {card.tagline}
        </p>

        {card.activities && card.activities.length > 0 && (
          <ul className="mt-6 flex flex-wrap justify-center gap-2" data-testid="vcard-activities">
            {card.activities.filter(Boolean).map((a) => (
              <li
                key={a}
                className="border border-foreground/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
              >
                {a}
              </li>
            ))}
          </ul>
        )}

        <p className="mt-8 flex items-start justify-center gap-3 text-sm text-muted-foreground" data-testid="vcard-address">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span className="text-left">
            {card.company}
            <br />
            {card.street}
            <br />
            {card.city}
          </span>
        </p>

        <div className="mt-10 space-y-3">
          <ActionButton
            href="#"
            onClick={downloadVcard}
            icon={UserPlus}
            label="Kontakt speichern"
            sub="Direkt ins Telefonbuch"
            primary
            center
            testid="vcard-download-btn"
          />
          <ActionButton
            href={`tel:${card.phone.replace(/\s+/g, "")}`}
            icon={Phone}
            label="Anrufen"
            sub={card.phone}
            testid="vcard-call-btn"
          />
          <ActionButton
            href={`https://wa.me/${card.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(card.whatsapp_text)}`}
            icon={MessageCircle}
            label="WhatsApp"
            sub="Direkt chatten"
            testid="vcard-whatsapp-btn"
          />
          <ActionButton
            href={`mailto:${card.email}`}
            icon={Mail}
            label="E-Mail"
            sub={card.email}
            testid="vcard-email-btn"
          />
          <ActionButton
            href={card.website}
            icon={Globe}
            label="Website"
            sub={card.website.replace(/^https?:\/\//, "")}
            testid="vcard-website-btn"
          />
        </div>

        <p className="mt-12 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60">
          {card.company} · {card.city}
        </p>
      </div>
    </div>
  );
}
